import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Plot from 'react-plotly.js';

const DQResultModal = ({ open, onClose, assetResults, assetName }) => {
  if (!assetResults) return null;

  const { rules, latest_dimension_scores, score_trend_plot_config } = assetResults;
  const qualityScore = latest_dimension_scores?.score || 0;
  const totalRecords = rules?.length || 0;
  const failedRecords = rules?.filter(rule => rule.dq_result !== "pass")?.length || 0;

  const getQualityScoreColor = (score) => {
    if (score > 85) return '#4CAF50';
    if (score >= 55) return '#FF9800';
    if (score < 55) return '#F44336';
    return '#000';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <div className='flex justify-between items-center'>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>DQ Results - {assetName}</DialogTitle>
        <IconButton sx={{marginRight: '20px'}} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent>
        <div>
          {/* Disclaimer Section */}
          <Box
            sx={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Box
              sx={{
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#fff3cd',
                border: '2px solid #856404'
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  lineHeight: 1
                }}
              >
                ⚠
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#856404',
                  marginBottom: '4px',
                  lineHeight: 1.4
                }}
              >
                Disclaimer
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#856404',
                  lineHeight: 1.5,
                  opacity: 0.9
                }}
              >
                If there are no other rules apart from row count and schema check, results will not be available. Please add more rules to see complete results.
              </Typography>
            </Box>
          </Box>

          {/* Stats Cards Row */}
          <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                padding: 3,
                width: '200px',
                height: '120px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                border: '1px solid #d3d3d3',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                  transform: 'translateY(-6px) !important',
                  border: '1px solid #0000a0 !important',
                  backgroundColor: '#f8f9ff'
                }
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                >
                  DQ Score
                </Typography>
                <Box
                  sx={{
                    height: '3px',
                    width: '40px',
                    backgroundColor: '#0000a0',
                    borderRadius: '2px',
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{color: getQualityScoreColor(qualityScore), fontWeight: 700, fontSize: '2rem'}}
              >
                {qualityScore}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                padding: 3,
                width: '200px',
                height: '120px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                border: '1px solid #d3d3d3',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                  transform: 'translateY(-6px) !important',
                  border: '1px solid #0000a0 !important',
                  backgroundColor: '#f8f9ff'
                }
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                >
                  Rules Processed
                </Typography>
                <Box
                  sx={{
                    height: '3px',
                    width: '40px',
                    backgroundColor: '#0000a0',
                    borderRadius: '2px',
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
              >
                {totalRecords}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                padding: 3,
                width: '200px',
                height: '120px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                border: '1px solid #d3d3d3',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                  transform: 'translateY(-6px) !important',
                  border: '1px solid #0000a0 !important',
                  backgroundColor: '#f8f9ff'
                }
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                >
                  Rules Failed
                </Typography>
                <Box
                  sx={{
                    height: '3px',
                    width: '40px',
                    backgroundColor: '#0000a0',
                    borderRadius: '2px',
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
              >
                {failedRecords}
              </Typography>
            </Box>
          </Box>

          {/* Two Column Layout */}
          <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
            {/* Left Column - Weightage */}
            <Box 
              sx={{ 
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                padding: 3,
                border: '1px solid #d3d3d3',
                flex: 1,
                minWidth: 0
              }}
            >
              <Typography 
                variant="h6" 
                sx={{
                  color: '#0000a0',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '20px',
                    backgroundColor: '#0000a0',
                    borderRadius: '2px'
                  }}
                />
                Dimension Level Score
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: 3,
                maxWidth: '100%'
              }}>
                {latest_dimension_scores && Object.entries(latest_dimension_scores)
                  .filter(([key]) => key.endsWith('_weightage'))
                  .map(([key, value]) => {
                    const dimensionName = key.replace('_weightage', '');
                    const score = latest_dimension_scores[dimensionName] || 0;
                    const weightageValue = value;
                    const contribution = score > 0 && weightageValue > 0 ? (score * weightageValue / 100).toFixed(2) : 0;
                    
                    return (
                      <Box 
                        key={key} 
                        sx={{
                          backgroundColor: score > 0 ? '#f0f9ff' : '#fef2f2',
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          padding: 3,
                          border: '1px solid',
                          borderColor: score > 0 ? '#e0f2fe' : '#fecaca',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        <Typography 
                          variant="h6" 
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#1f2937',
                            marginBottom: 2,
                            textTransform: 'uppercase',
                            textAlign: 'center'
                          }}
                        >
                          {dimensionName.charAt(0).toUpperCase() + dimensionName.slice(1)}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#6b7280' }}>
                              Score
                            </Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: score > 0 ? '#059669' : '#dc2626' }}>
                              {score}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#6b7280' }}>
                              Weightage
                            </Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#374151' }}>
                              {weightageValue}%
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#6b7280' }}>
                              Contribution
                            </Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: score > 0 ? '#059669' : '#dc2626' }}>
                              {contribution}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>

            {/* Right Column - Chart */}
            <Box 
              sx={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                padding: 3,
                border: '1px solid #d3d3d3',
                flex: 1,
                minWidth: 0
              }}
            >
              <Typography 
                variant="h6" 
                sx={{
                  color: '#0000a0',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '20px',
                    backgroundColor: '#0000a0',
                    borderRadius: '2px'
                  }}
                />
                DQ Score Trend
              </Typography>
              <Box sx={{ width: '100%', height: '300px' }}>
                <Plot 
                  data={score_trend_plot_config?.data || []}
                  layout={{
                    ...(score_trend_plot_config?.layout || {}),
                    margin: { t: 40, b: 60, l: 60, r: 40 },
                    autosize: true,
                    height: 280
                  }}
                  style={{ width: '100%', height: '100%' }}
                  useResizeHandler={true}
                />
              </Box>
            </Box>
          </Box>

          {/* Results Table */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
              border: '1px solid #e0e0e0', 
              borderRadius: '12px',
              marginTop: "30px",
              maxHeight: '500px',
              overflow: 'auto'
            }}
          >
            <Table sx={{ minWidth: 650 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Column Name
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Rule Description
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Dimension
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Keywords
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0000a0 !important'
                  }}>
                    Score
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules?.length > 0 ? (
                  rules.map((rule, index) => (
                    <TableRow 
                      key={rule.rule_name}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                        '&:hover': {
                          backgroundColor: '#f0f8ff',
                          transition: 'background-color 0.2s ease'
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#2c3e50'
                      }}>
                        {rule.check_type === "Asset Level Check" ? "Asset Level Rule" : rule.column_name}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        maxWidth: '300px',
                        wordWrap: 'break-word'
                      }}>
                        {rule.rule_description}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        textTransform: 'capitalize'
                      }}>
                        {rule.dimension_name.charAt(0).toUpperCase() + rule.dimension_name.slice(1)}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        textTransform: 'capitalize'
                      }}>
                        {rule.keywords}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px'
                      }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 2,
                            py: 0.5,
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            backgroundColor: rule.dq_result === "pass" ? '#e8f5e8' : '#ffeaea',
                            color: rule.dq_result === "pass" ? '#2e7d32' : '#d32f2f'
                          }}
                        >
                          {rule.dq_result === "pass" ? "Pass" : rule.dq_result === "fail" || rule.dq_result === "FAILURE" ? "Fail" : "N/A"}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: getQualityScoreColor(rule.quality_score)
                      }}>
                        {rule.quality_score}%
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={6} 
                      align="center"
                      sx={{ 
                        padding: '40px 20px',
                        fontSize: '16px',
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      No rules found for the selected dataset.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DQResultModal; 